// D110's fair use on the Plan tab (APS-350): each limit this month with what it used, a callout
// when one is near (80%) or reached, and what applies now (sampled summaries or errors, a
// telemetry rate, OTLP without a session refused, symbol uploads refused). Its own section,
// mounted by PlanSettings; reads GET /orgs/{id}/usage's fair_use.
import type { FairUseLimit, FairUseView, Usage } from '../api/types'
import { Section } from '../components/d'
import { type MessageId, useI18n } from '../i18n'

const LIMITS: Record<FairUseLimit, MessageId> = {
  summaries: 'fairUse.limit.summaries',
  errors: 'fairUse.limit.errors',
  telemetry: 'fairUse.limit.telemetry',
  sessionless: 'fairUse.limit.sessionless',
  symbols: 'fairUse.limit.symbols',
}
const NEAR: Record<FairUseLimit, MessageId> = {
  summaries: 'fairUse.near.summaries',
  errors: 'fairUse.near.errors',
  telemetry: 'fairUse.near.telemetry',
  sessionless: 'fairUse.near.sessionless',
  symbols: 'fairUse.near.symbols',
}
const OVER: Record<FairUseLimit, MessageId> = {
  summaries: 'fairUse.over.summaries',
  errors: 'fairUse.over.errors',
  telemetry: 'fairUse.over.telemetry',
  sessionless: 'fairUse.over.sessionless',
  symbols: 'fairUse.over.symbols',
}

type Level = FairUseView['levels'][number]

const isBytes = (l: FairUseLimit) => l === 'sessionless' || l === 'symbols'

export function FairUse({ usage }: { usage: Usage }) {
  const { t, number } = useI18n()
  const fair = usage.fair_use
  if (!fair) return null
  const amount = (l: Level, n: number) =>
    isBytes(l.limit)
      ? `${number(n / 1e9, { maximumFractionDigits: 1 })} GB`
      : number(n, { maximumFractionDigits: 0 })
  const allowed = (l: Level) =>
    l.allowed === null
      ? t('fairUse.noLimit')
      : l.allowed === 0
        ? t('fairUse.notIncluded')
        : amount(l, l.allowed)
  const alerts = fair.levels.filter((l) => l.allowed && l.share !== null && l.share >= 0.8)
  const r = fair.restrictions
  const notes: string[] = [
    ...(r.summary_rate ? [t('fairUse.applies.summaries', { rate: r.summary_rate })] : []),
    ...(r.error_rate ? [t('fairUse.applies.errors', { rate: r.error_rate })] : []),
    ...(r.telemetry_rate ? [t('fairUse.applies.telemetry', { rate: r.telemetry_rate })] : []),
    ...(r.sessionless_refused ? [t('fairUse.applies.sessionless')] : []),
    ...(r.symbols_full ? [t('fairUse.applies.symbols')] : []),
  ]
  return (
    <Section title={t('fairUse.title')} meta={fair.month}>
      {alerts.length > 0 ? (
        <div className="callouts">
          {alerts.map((l) => (
            <div key={l.limit} className="callout">
              <span className={(l.share ?? 0) >= 1 ? 'g crash' : 'g'} aria-hidden="true">
                !
              </span>
              <div>{t((l.share ?? 0) >= 1 ? OVER[l.limit] : NEAR[l.limit])}</div>
            </div>
          ))}
        </div>
      ) : null}
      <div className="fair-use">
        {fair.levels.map((l) => (
          <div key={l.limit} className="se-meter">
            <div className="r">
              <span>{t(LIMITS[l.limit])}</span>
              <span>
                <b>{amount(l, l.used)}</b> / {allowed(l)}
              </span>
            </div>
            {l.allowed ? (
              // biome-ignore lint/a11y/useSemanticElements: <meter> cannot take the console's meter-bar styling (an <i> fill)
              <span
                className={(l.share ?? 0) >= 0.8 ? 'meter-bar warn' : 'meter-bar'}
                role="meter"
                aria-label={t(LIMITS[l.limit])}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.min(100, Math.round((l.share ?? 0) * 100))}
              >
                <i style={{ width: `${Math.min(100, (l.share ?? 0) * 100)}%` }} />
              </span>
            ) : null}
          </div>
        ))}
      </div>
      {notes.length > 0 ? (
        <ul className="se-hint">
          {notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      ) : (
        <p className="se-hint">{t('fairUse.within')}</p>
      )}
    </Section>
  )
}
